import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { fetchProduct } from '../../products/services/ProductsService';
import { fetchPricesByProduct } from '../../prices/services/PricesService';
import { createSubscription } from '../services/SubscriptionsService';
import { useUserAccount } from '../../../../hooks/useUserAccount';

export const useCreateSubscription = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { userAccount } = useUserAccount();
    
    const productId = searchParams.get('productId');
    const priceId = searchParams.get('priceId');
    const returnTo = searchParams.get('returnTo');
    const providerKey = searchParams.get('providerKey') || 'stripe';
    const paypalSubscriptionId = searchParams.get('subscription_id');

    // Handle PayPal return flow
    useEffect(() => {
        if (paypalSubscriptionId && userAccount?.customerId && priceId) {
            createSubscription({
                customerId: userAccount.customerId,
                items: [{ priceId, quantity: 1 }],
                status: 'Active'
            }).then(() => navigate(returnTo || '/subscriptions'))
              .catch(err => console.error('Failed to create subscription:', err));
        }
    }, [paypalSubscriptionId, userAccount?.customerId, priceId, returnTo, navigate]);

    const { data: product, isLoading: productLoading } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => fetchProduct(productId as string),
        enabled: !!productId
    });

    const { data: prices, isLoading: pricesLoading } = useQuery({
        queryKey: ['prices', productId],
        queryFn: () => fetchPricesByProduct(productId as string),
        enabled: !!productId
    });

    const price = prices?.find((p) => p.id === priceId);

    const createMutation = useMutation({
        mutationFn: async () => {
            if (!priceId) {
                throw new Error('priceId is required to create a subscription');
            }
            // Billing API handles provider-specific logic
            return await createSubscription({
                customerId: userAccount?.customerId ?? '',
                items: [{ priceId, quantity: 1 }],
                status: 'Active'
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            navigate(returnTo || '/subscriptions');
        },
        onError: (error: unknown) => {
            console.error('Subscription creation failed:', error);
            const err = error as { errors?: { message?: string }[]; message?: string } | undefined;
            const errorMessage = err?.errors?.[0]?.message || err?.message || String(error);

            if (errorMessage.includes('timed out')) {
                alert('Subscription creation is taking longer than expected. Please check your subscriptions page in a moment.');
            } else {
                alert(`Subscription creation failed: ${errorMessage}`);
            }
        }
    });

    const displayAmount = (price?.amount || 0) / 100;
    const displayPeriod = price?.billingPeriod || 'month';

    return {
        product,
        price,
        displayAmount,
        displayPeriod,
        providerKey,
        loading: productLoading || pricesLoading || !!paypalSubscriptionId,
        createSubscription: createMutation.mutate,
        isCreating: createMutation.isPending,
        handleCancel: () => navigate(returnTo || '/products')
    };
};
